import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Interactive mini-map: click (or drag the pin) to set a facility's coordinates.
// Two-way synced with the lat/lng number inputs in the parent form.
export function FacilityLocationPicker({ lat, lng, onChange }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const placeRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!ref.current || mapRef.current) return undefined;
    const hasStart = lat != null && lng != null && !Number.isNaN(+lat) && !Number.isNaN(+lng);
    const map = L.map(ref.current, { scrollWheelZoom: false }).setView(hasStart ? [+lat, +lng] : [4, 110], hasStart ? 9 : 4);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap contributors" }).addTo(map);
    const dot = L.divIcon({ className: "", html: '<div style="width:16px;height:16px;border-radius:50%;background:#0F7B4E;border:2px solid #fff;box-shadow:0 0 5px rgba(0,0,0,.5)"></div>' });
    const place = (ll, emit) => {
      if (markerRef.current) markerRef.current.setLatLng(ll);
      else {
        markerRef.current = L.marker(ll, { icon: dot, draggable: true }).addTo(map);
        markerRef.current.on("dragend", () => {
          const p = markerRef.current.getLatLng();
          onChangeRef.current({ lat: +p.lat.toFixed(5), lng: +p.lng.toFixed(5) });
        });
      }
      if (emit) onChangeRef.current({ lat: +ll[0].toFixed(5), lng: +ll[1].toFixed(5) });
    };
    placeRef.current = place;
    if (hasStart) place([+lat, +lng], false);
    map.on("click", (e) => place([e.latlng.lat, e.latlng.lng], true));
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 120);
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; placeRef.current = null; };
  }, []); // eslint-disable-line

  // Reflect coordinates typed into the inputs onto the map (no re-emit → no loop).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !placeRef.current) return;
    if (lat != null && lng != null && lat !== "" && lng !== "" && !Number.isNaN(+lat) && !Number.isNaN(+lng)) {
      placeRef.current([+lat, +lng], false);
      map.setView([+lat, +lng], Math.max(map.getZoom(), 8));
    }
  }, [lat, lng]);

  return <div ref={ref} className="h-[220px] rounded-md border border-border overflow-hidden" data-testid="facility-location-map" />;
}
